import React, { useState, useEffect } from 'react';
import './TradeHistory.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import TradeSides, { useCardInfo } from '../Requests/TradeSides';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { ReviewShop } from '../Shipping/ReviewShop';

/**
 * THE FULL STORY OF ONE TRADE - a devoted page per trade:
 *   1. The trade itself (partner, get/give strips, note)
 *   2. Shipping, written out per leg (method, middleman, every
 *      stage with its timestamp)
 *   3. The complete chronological timeline - every event from
 *      "offer sent" through documentation, packages, middleman
 *      hand-offs, to delivered/finished (or cancelled and why)
 * Linked from the dashboard's history tickets; shipping actions
 * (reviews, binder cleanup) stay on the shipping page.
 */

const LEG_FLOWS = {
    shop: ['shipped', 'shop_received', 'shop_verified', 'forwarded', 'delivered'],
    direct: ['shipped', 'delivered'],
};

// Pickup legs (only their receiver sees dest_type): at the verifying
// shop there's no forwarding; at another shop there's an arrival step
function legFlow(leg) {
    if (leg.method !== 'shop') return LEG_FLOWS.direct;
    if (leg.dest_type !== 'shop') return LEG_FLOWS.shop;
    return leg.dest_shop_id && leg.dest_shop_id !== leg.shop_id
        ? ['shipped', 'shop_received', 'shop_verified', 'forwarded', 'at_pickup', 'delivered']
        : ['shipped', 'shop_received', 'shop_verified', 'delivered'];
}

const LEG_LABELS = {
    shipped: 'Shipped',
    shop_received: 'Received by middleman',
    shop_verified: 'Contents verified',
    forwarded: 'Forwarded on',
    at_pickup: 'Ready for pickup',
    delivered: 'Delivered',
};

const LEG_TIMES = {
    shipped: 'shipped_at',
    shop_received: 'shop_received_at',
    shop_verified: 'shop_verified_at',
    forwarded: 'forwarded_at',
    at_pickup: 'at_pickup_at',
    delivered: 'delivered_at',
};

function TradeHistory() {
    const { tradeId } = useParams();
    const { user } = useUser();
    const navigate = useNavigate();

    const [trade, setTrade] = useState(null);
    const [events, setEvents] = useState(null); // { events, shops }
    const [shipping, setShipping] = useState(null); // null = none/never started
    const [partner, setPartner] = useState(null);
    const [error, setError] = useState(null);

    // Wrap-up actions done during THIS visit (the server remembers
    // between visits via ratedShopIds / binderCleaned in the payload)
    const [rated, setRated] = useState({});
    const [binderCleaned, setBinderCleaned] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [notice, setNotice] = useState(null);

    const { openCardInfo, cardInfoElement } = useCardInfo();

    useEffect(() => {
        if (!user) return;
        let alive = true;
        (async () => {
            try {
                const opts = { credentials: 'include' };
                const [tRes, eRes, sRes] = await Promise.all([
                    fetch(`/api/trade-requests/${tradeId}`, opts),
                    fetch(`/api/trade-requests/${tradeId}/events`, opts),
                    fetch(`/api/trade-requests/${tradeId}/shipping`, opts),
                ]);
                if (!tRes.ok) {
                    const d = await tRes.json().catch(() => ({}));
                    throw new Error(d.error || 'Failed to load the trade.');
                }
                const t = (await tRes.json()).request;
                if (!alive) return;
                setTrade(t);
                setEvents(eRes.ok ? await eRes.json() : { events: [], shops: [] });
                // 400 = shipping never started (offer declined/withdrawn)
                setShipping(sRes.ok ? await sRes.json() : null);

                const otherId = t.ownerId === user.id ? t.otherId : t.ownerId;
                if (otherId) {
                    const pRes = await fetch(`/api/users/${otherId}/public`);
                    if (pRes.ok && alive) setPartner((await pRes.json()).user);
                }
            } catch (err) {
                if (alive) setError(err.message);
            }
        })();
        return () => { alive = false; };
    }, [tradeId, user]);

    // Turn middleman names inside event text into store-page links
    const linkifyShops = (text, shops) => {
        if (!text || !shops?.length) return text;
        let parts = [text];
        let keyIdx = 0;
        shops.forEach(s => {
            if (!s.name) return;
            parts = parts.flatMap(part => {
                if (typeof part !== 'string' || !part.includes(s.name)) return [part];
                const segs = part.split(s.name);
                const out = [];
                segs.forEach((seg, i) => {
                    if (i > 0) {
                        out.push(
                            <Link key={`shoplink-${s.id}-${keyIdx++}`} to={`/store/${s.id}`} className="order-history-shoplink">
                                {s.name}
                            </Link>
                        );
                    }
                    if (seg) out.push(seg);
                });
                return out;
            });
        });
        return parts;
    };

    const eventTone = (type) => {
        if (['cancelled', 'declined', 'shop_rejected'].includes(type)) return 'bad';
        if (['completed', 'delivered', 'accepted'].includes(type)) return 'good';
        return '';
    };

    const fmtDay = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const fmtStamp = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    if (!user) {
        return <LoginGate title="Trades" section="History" message="Log in to view this trade." />;
    }
    if (error) {
        return (
            <div className="requests-container">
                <div className="requests-placeholder">
                    {error} <Link to="/requests">Back to trades</Link>
                </div>
            </div>
        );
    }
    if (!trade) {
        return <div className="requests-container"><div className="requests-placeholder">Loading trade&hellip;</div></div>;
    }

    const iAmOther = trade.otherId === user.id;
    const myCards = iAmOther ? trade.otherTrade : trade.ownerTrade;
    const theirCards = iAmOther ? trade.ownerTrade : trade.otherTrade;

    const statusChip = trade.status === 'cancelled'
        ? <span className="tk-chip bad">Cancelled</span>
        : trade.status === 'finished'
            ? <span className="tk-chip done">Finished</span>
            : trade.status === 'pending'
                ? <span className="tk-chip">Open offer</span>
                : <span className="tk-chip gold">In progress</span>;

    const usernames = shipping?.usernames || {};
    const nameOf = (id) => (id === user.id ? 'You' : (usernames[id] || partner?.username || 'them'));

    // A middleman, written out with its store link, address and fee
    const shopLine = (shopId) => {
        const shop = shipping?.shops?.[shopId];
        if (!shop) return 'a middleman';
        return (
            <>
                <Link to={`/store/${shop.id}`} className="order-history-shoplink">{shop.name}</Link>
                {shop.address ? <> · {shop.address}</> : null}
                {shop.fee_info ? <> · {shop.fee_info}</> : null}
            </>
        );
    };

    const renderLeg = (leg) => {
        const flow = legFlow(leg);
        const shop = leg.shop_id ? shipping?.shops?.[leg.shop_id] : null;
        const rejected = leg.status === 'shop_rejected';
        const notStarted = ['pending', 'ready'].includes(leg.status);

        return (
            <div className="th-leg" key={leg.id}>
                <div className="th-leg-head">
                    <span className="th-leg-route">{nameOf(leg.sender_id)} → {nameOf(leg.receiver_id)}</span>
                    <span className="th-leg-method">
                        {leg.method === 'shop' && shop
                            ? <>via <Link to={`/store/${shop.id}`} className="order-history-shoplink">{shop.name}</Link></>
                            : leg.method === 'shop' ? 'via middleman' : 'ships direct'}
                    </span>
                </div>
                {rejected ? (
                    <div className="th-leg-rejected">
                        ✕ Rejected by the middleman{leg.shop_rejected_at ? ` · ${fmtStamp(leg.shop_rejected_at)}` : ''}
                    </div>
                ) : notStarted ? (
                    <div className="th-leg-idle">Not shipped{trade.status === 'cancelled' ? ' - trade was cancelled first' : ' yet'}</div>
                ) : (
                    <ol className="th-leg-steps">
                        {flow.map((s) => {
                            const at = leg[LEG_TIMES[s]];
                            const done = !!at || flow.indexOf(s) <= flow.indexOf(leg.status);
                            return (
                                <li key={s} className={done ? 'done' : ''}>
                                    <span className="th-step-dot">{done ? '✓' : ''}</span>
                                    <span className="th-step-label">{LEG_LABELS[s]}</span>
                                    {at && <span className="th-step-time">{fmtStamp(at)}</span>}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        );
    };

    /**
     * The whole shipping flow, written out step by step - everything
     * the shipping page walks you through, as a readable record with
     * store links instead of a click-through. Steps the trade never
     * reached say so.
     */
    const renderFlow = () => {
        const { prefs, setup, docs, legs = [], middlemanRequired, docsRequired, step } = shipping;
        const cancelled = trade.status === 'cancelled';
        const finished = trade.status === 'finished';
        const them = partner?.username || 'They';

        const decideBody = prefs?.bothChosen ? (
            <>
                <p>
                    You chose <strong>{prefs.mine === 1 ? 'middleman' : 'no middleman'}</strong> ·{' '}
                    {them} chose <strong>{prefs.theirs === 1 ? 'middleman' : 'no middleman'}</strong>
                </p>
                <p>
                    → {middlemanRequired
                        ? 'Both sides ship through verified middlemen, who check the cards in person (so there is no photo-documentation step).'
                        : 'No middlemen: both sides ship directly to each other.'}
                </p>
            </>
        ) : (
            <p>
                {prefs?.mine !== null && prefs?.mine !== undefined
                    ? <>You chose <strong>{prefs.mine === 1 ? 'middleman' : 'no middleman'}</strong>; the other side never answered.</>
                    : 'Neither side finished the middleman decision.'}
            </p>
        );

        const setupBody = (
            <>
                <p>
                    <strong>You:</strong>{' '}
                    {setup?.myComplete ? (
                        <>
                            {middlemanRequired && <>drop your cards at {shopLine(setup.mine.shopId)} · </>}
                            {setup.mine.pickup
                                ? <>you pick your incoming cards up at your middleman</>
                                : <>incoming cards deliver to <strong>{setup.mine.resolved || setup.mine.address}</strong></>}
                        </>
                    ) : 'setup never submitted.'}
                </p>
                <p>
                    {/* How they receive (mail or pickup) stays theirs alone */}
                    <strong>{them}:</strong>{' '}
                    {setup?.theirsComplete
                        ? (middlemanRequired
                            ? <>drops their cards at {shopLine(setup.theirs.shopId)}</>
                            : <>setup complete ✓</>)
                        : 'setup never submitted.'}
                </p>
            </>
        );

        const docsBody = (
            <>
                <p>
                    {docs?.complete
                        ? 'Both sides photographed their cards and approved each other\'s documentation - the agreed record of what this trade contains.'
                        : docs?.mine?.submitted && docs?.theirsSubmitted
                            ? 'Both sides submitted documentation, but it was never fully approved.'
                            : docs?.mine?.submitted
                                ? `You submitted documentation; ${them} never did.`
                                : docs?.theirsSubmitted
                                    ? `${them} submitted documentation; you never did.`
                                    : 'No documentation was submitted.'}
                </p>
                {(docs?.mine?.photos?.length > 0 || docs?.theirs?.photos?.length > 0) && (
                    <div className="th-docs">
                        {docs.mine?.photos?.length > 0 && (
                            <figure>
                                <figcaption>Your cards</figcaption>
                                <div className="th-docs-photos">
                                    {docs.mine.photos.map(p => (
                                        <a key={p} href={p} target="_blank" rel="noreferrer">
                                            <img src={p} alt="your documented cards" />
                                        </a>
                                    ))}
                                </div>
                                {docs.mine.description && <p className="th-docs-desc">“{docs.mine.description}”</p>}
                            </figure>
                        )}
                        {docs.theirs?.photos?.length > 0 && (
                            <figure>
                                <figcaption>{them}'s cards</figcaption>
                                <div className="th-docs-photos">
                                    {docs.theirs.photos.map(p => (
                                        <a key={p} href={p} target="_blank" rel="noreferrer">
                                            <img src={p} alt={`${them}'s documented cards`} />
                                        </a>
                                    ))}
                                </div>
                                {docs.theirs.description && <p className="th-docs-desc">“{docs.theirs.description}”</p>}
                            </figure>
                        )}
                    </div>
                )}
            </>
        );

        const shipBody = legs.length > 0 ? (
            <div className="th-legs">{legs.map(renderLeg)}</div>
        ) : (
            <p>No packages were ever sent.</p>
        );

        const steps = [
            { key: 'decide', title: 'Decide on a middleman', internal: 1, body: decideBody },
            { key: 'setup', title: 'Shipping setup', internal: 2, body: setupBody },
            ...(docsRequired !== false
                ? [{ key: 'docs', title: 'Document the cards', internal: 3, body: docsBody }]
                : []),
            { key: 'ship', title: 'Ship', internal: 4, body: shipBody },
        ];

        return (
            <ol className="th-flow">
                {steps.map((s, i) => {
                    const state = finished ? 'done'
                        : s.internal < step ? 'done'
                            : s.internal > step ? 'never'
                                : cancelled ? 'stopped' : 'current';
                    return (
                        <li key={s.key} className={`th-flow-step ${state}`}>
                            <div className="th-flow-head">
                                <span className="th-flow-dot">
                                    {state === 'done' ? '✓' : state === 'stopped' ? '✕' : i + 1}
                                </span>
                                <span className="th-flow-title">Step {i + 1} · {s.title}</span>
                                {state === 'current' && <span className="th-flow-note gold">in progress</span>}
                                {state === 'stopped' && <span className="th-flow-note bad">the trade was cancelled here</span>}
                                {state === 'never' && <span className="th-flow-note">never reached</span>}
                            </div>
                            {state !== 'never' && <div className="th-flow-body">{s.body}</div>}
                        </li>
                    );
                })}
            </ol>
        );
    };

    // Wrap-up: what this trader hasn't chosen to do yet. Stays
    // available here for as long as they like - never a notification.
    const unratedShopIds = shipping
        ? [...new Set((shipping.legs || []).map(l => l.shop_id).filter(Boolean))]
            .filter(id => !(shipping.ratedShopIds || []).includes(id))
        : [];
    const showWrapUp = trade.status === 'finished' && shipping &&
        (!shipping.binderCleaned || (shipping.canRate && unratedShopIds.length > 0));

    const tidyBinders = () => {
        setDialog({
            title: 'Update your binders?',
            message: 'Removes the cards you sent from your collection and the cards you received from your wishlist.',
            confirmLabel: 'Update binders',
            action: async () => {
                setBusy(true);
                try {
                    const res = await fetch(`/api/trade-requests/${trade.tradeId}/binder-cleanup`, {
                        method: 'POST', credentials: 'include',
                    });
                    const json = await res.json();
                    if (res.status === 409) { setBinderCleaned(true); return; }
                    if (!res.ok) throw new Error(json.error || 'Failed to update binders.');
                    setBinderCleaned(true);
                    setNotice({
                        title: 'Binders updated',
                        message: `Removed ${json.removedHaves} card${json.removedHaves !== 1 ? 's' : ''} from your collection and ${json.removedWants} from your wishlist.`,
                    });
                } catch (e) {
                    setNotice({ title: 'Update failed', message: e.message });
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    return (
        <div className="requests-container th-page">
            <header className="page-section-head th-head">
                <h1>
                    Trade <span className="page-section-current">{trade.trade_code || `#${trade.tradeId}`}</span>
                </h1>
                {statusChip}
                <p className="dash-summary th-sub">
                    with{' '}
                    {partner
                        ? <Link to={`/profile/${partner.id}`} className="th-partner-link">{partner.username}</Link>
                        : '…'}
                    {' '}· opened {fmtDay(trade.created_at)}
                </p>
            </header>

            {/* Two columns on wide screens: the trade and its shipping story
                on the left, wrap-up + the full timeline beside it */}
            <div className="th-layout">
            <div className="th-main">
            {/* 1. The trade itself */}
            <article className="tk th-summary">
                <header className="tk-head">
                    <button className="tk-who" onClick={() => partner && navigate(`/profile/${partner.id}`)}>
                        <AvatarFrame user={partner}>
                            <img
                                src={partner ? getProfilePhotoSrc(partner) : PLACEHOLDER_DATA_URI}
                                alt={partner?.username || 'trader'}
                                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                            />
                        </AvatarFrame>
                        <span className="tk-who-text">
                            <span className="tk-name">{partner?.username || '…'}</span>
                            <span className="tk-meta">{fmtDay(trade.created_at)} · {trade.trade_code}</span>
                        </span>
                    </button>
                    {statusChip}
                </header>
                <TradeSides getCards={theirCards} giveCards={myCards} onCardClick={openCardInfo} />
                {trade.note && (
                    <p className="tk-note">
                        <span className="tk-note-label">Note</span>
                        “{trade.note}”
                    </p>
                )}
                {trade.status === 'cancelled' && (
                    <div className="tk-cancel">✕ Cancelled: {trade.cancel_reason || 'no reason recorded'}</div>
                )}
            </article>

            {/* 2. Shipping, written out step by step */}
            <section className="th-sec">
                <h2>Shipping</h2>
                {!shipping ? (
                    <div className="th-empty">Shipping never started for this trade.</div>
                ) : (
                    <>
                        {renderFlow()}
                        {['accepted', 'processing'].includes(trade.status) && (
                            <button className="tk-btn gold th-ship-btn" onClick={() => navigate(`/shipping/${trade.tradeId}`)}>
                                This trade is still moving. Act on the shipping page
                            </button>
                        )}
                    </>
                )}
            </section>
            </div>

            <aside className="th-side">
            {/* Whatever wrap-up the trader hasn't chosen to do yet */}
            {showWrapUp && (
                <section className="th-sec">
                    <h2>Wrap-up</h2>
                    <div className="th-wrapup">
                        {!shipping.binderCleaned && (
                            <div className="ship-rate-block">
                                <div className="ship-rate-row">
                                    <span>Update my binders</span>
                                    {binderCleaned
                                        ? <span className="ship-hint">✓ Binders updated</span>
                                        : (
                                            <button className="cta-gold ship-rate-submit" disabled={busy} onClick={tidyBinders}>
                                                Update binders
                                            </button>
                                        )}
                                </div>
                                <p className="ship-hint">
                                    Removes the cards you sent from your collection and the cards you
                                    received from your wishlist. Whenever you're ready.
                                </p>
                            </div>
                        )}
                        {shipping.canRate && unratedShopIds.map(id => (
                            <ReviewShop
                                key={id}
                                shopId={id}
                                shopName={shipping.shops?.[id]?.name || `Shop #${id}`}
                                tradeId={trade.tradeId}
                                done={!!rated[id]}
                                onDone={(sid) => setRated(r => ({ ...r, [sid]: true }))}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* 3. The complete timeline */}
            <section className="th-sec">
                <h2>Full timeline</h2>
                {!events ? (
                    <div className="th-empty">Loading&hellip;</div>
                ) : events.events.length === 0 ? (
                    <div className="th-empty">No detailed history recorded. This trade predates event tracking.</div>
                ) : (
                    <ol className="order-history th-timeline">
                        {events.events.map((ev, i) => (
                            <li key={i} className={eventTone(ev.type)}>
                                <span className="order-history-dot" />
                                <span className="order-history-detail">
                                    {linkifyShops(ev.detail || ev.type.replace(/_/g, ' '), events.shops)}
                                </span>
                                <span className="order-history-time">{fmtStamp(ev.created_at)}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </section>
            </aside>
            </div>

            {cardInfoElement}

            <ConfirmDialog
                open={!!dialog}
                title={dialog?.title}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                danger={dialog?.danger}
                busy={busy}
                onConfirm={() => { const a = dialog.action; setDialog(null); a(); }}
                onCancel={() => setDialog(null)}
            />
            <ConfirmDialog
                open={!!notice}
                title={notice?.title}
                message={notice?.message}
                onCancel={() => setNotice(null)}
            />
        </div>
    );
}

export default TradeHistory;
